import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchAnalytics } from "@admin/services/adminApi";
import { PageHeader, StatCard } from "@admin/components/Primitives";
import { DateRangeFilter } from "@admin/components/analytics/DateRangeFilter";
import { MetricCard, SectionTitle, EconTable, paise, num } from "@admin/components/analytics/AnalyticsPrimitives";

const TIPS = {
  gmv: "Total amount paid by customers, including GST. Gross cohort: paid + refunded bookings.",
  refunded: "Amount refunded to customers (bookings with payment_status = refunded).",
  net: "Customer GMV − Refunded Amount.",
  revenue: "Customer consultation price before GST. Snapshot-backed paid bookings only.",
  gst: "GST included in customer payments. Not Advoset revenue.",
  fees: "Lawyer professional fee from the immutable booking pricing snapshot.",
  margin: "Customer price before GST minus lawyer professional fee (after discount, incl. rounding).",
  expenses: "Configured estimate: variable expense from the pricing config version frozen in each snapshot (Level 11: per-lawyer).",
  contribution: "Advoset gross margin minus configured variable expenses. GST excluded. Can be negative.",
  conversion: "Distinct AI-chat users (in range) who also paid for a consultation (in range) ÷ distinct AI-chat users. N/A when there are no chat users.",
};

const MODE_LABEL = { chat: "Chat", audio: "Audio", video: "Video" };

export default function AdminAnalytics() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ preset: "all", from: "", to: "" });

  useEffect(() => {
    const params = { range: range.preset };
    if (range.preset === "custom") {
      if (!range.from && !range.to) return;
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
    }
    setLoading(true);
    fetchAnalytics(params).then(setS).catch((e) => toast.error(e?.response?.data?.detail || "Could not load analytics")).finally(() => setLoading(false));
  }, [range]);

  if (!s) {
    return (
      <>
        <PageHeader testid="admin-analytics-header" title="Analytics" subtitle="Customer GMV, unit economics, consultations, conversion." />
        <DateRangeFilter value={range} onChange={setRange} />
        <div className="text-slate-500" data-testid="analytics-loading">Loading analytics…</div>
      </>
    );
  }

  const f = s.financial;
  const u = s.unit_economics;
  const ov = s.consultations_overview;
  const conv = s.conversion;
  const hasSnapshots = f.snapshot_count > 0;
  const levelRows = s.by_level.map((r) => ({ ...r, label: `Level ${r.level}${r.level === 11 ? " (custom)" : ""}` }));
  const modeRows = s.by_mode.map((r) => ({ ...r, label: MODE_LABEL[r.mode] || r.mode }));
  const durRows = s.by_duration.map((r) => ({ ...r, label: `${r.duration_minutes} min` }));

  return (
    <>
      <PageHeader testid="admin-analytics-header" title="Analytics" subtitle="Customer GMV, unit economics, consultations, conversion." />
      <DateRangeFilter value={range} onChange={setRange} loading={loading} />
      <div className={`transition-opacity duration-300 ${loading ? "opacity-50 pointer-events-none" : "opacity-100"}`} data-testid="analytics-body" aria-busy={loading}>

      <SectionTitle testid="section-financial" note={`Gross cohort: ${num(f.gross_count)} charged bookings · Economics: ${num(f.snapshot_count)} snapshot-backed`}>Financial overview</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
        <MetricCard testid="fin-gmv" label="Customer GMV" value={paise(f.customer_gmv_paise)} tip={TIPS.gmv} accent="navy" hint={`${num(f.paid_count)} paid · ${num(f.refunded_count)} refunded`} />
        <MetricCard testid="fin-refunded" label="Refunded Amount" value={paise(f.refunded_paise)} tip={TIPS.refunded} accent="rose" hint={`${num(f.refunded_count)} refunded bookings`} />
        <MetricCard testid="fin-net" label="Net Customer Collections" value={paise(f.net_collections_paise)} tip={TIPS.net} accent="gold" hint="GMV − refunds" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <MetricCard testid="fin-revenue" label="Revenue Before GST" value={paise(f.revenue_ex_gst_paise)} tip={TIPS.revenue} />
        <MetricCard testid="fin-gst" label="GST Collected" value={paise(f.gst_paise)} tip={TIPS.gst} />
        <MetricCard testid="fin-fees" label="Lawyer Fees" value={paise(f.lawyer_fees_paise)} tip={TIPS.fees} />
        <MetricCard testid="fin-margin" label="Advoset Gross Margin" value={paise(f.gross_margin_paise)} tip={TIPS.margin} negative={f.gross_margin_paise < 0} />
        <MetricCard testid="fin-expenses" label="Other Expenses" value={paise(f.other_expenses_paise)} tip={TIPS.expenses} hint="configured estimate" />
        <MetricCard testid="fin-contribution" label="Est. Contribution" value={paise(f.est_contribution_paise)} tip={TIPS.contribution} negative={f.est_contribution_paise < 0} accent={f.est_contribution_paise < 0 ? "rose" : "emerald"} />
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs text-slate-600 mb-8 flex flex-wrap gap-x-6 gap-y-1" data-testid="fin-cohort-note">
        <span><strong>Snapshot-backed:</strong> {num(f.snapshot_count)} bookings · {paise(f.snapshot_gmv_paise)} GMV — detailed split above uses the immutable booking snapshot.</span>
        <span data-testid="fin-legacy-note"><strong>Legacy / snapshot unavailable:</strong> {num(f.legacy_count)} bookings · {paise(f.legacy_gmv_paise)} GMV — included in Customer GMV only; fee/GST/margin split unknown, never estimated.</span>
        {f.expense_unknown_count > 0 && <span data-testid="fin-expense-unknown">* {num(f.expense_unknown_count)} bookings reference a pricing config version with no expense record — expenses omitted for them.</span>}
      </div>

      <SectionTitle testid="section-consultations">Consultation overview</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-8">
        <MetricCard testid="ov-paid" label="Paid" value={num(ov.paid)} hint={`of ${num(ov.total)} bookings`} accent="gold" />
        <MetricCard testid="ov-completed" label="Completed" value={num(ov.completed)} hint="closed successfully" accent="emerald" />
        <MetricCard testid="ov-active" label="Active" value={num(ov.active)} hint="confirmed / accepted / live" />
        <MetricCard testid="ov-pending" label="Pending" value={num(ov.pending)} hint={`${num(ov.pending_payment)} awaiting payment`} />
        <MetricCard testid="ov-cancelled" label="Cancelled" value={num(ov.cancelled)} hint={`${num(ov.no_show)} no-show`} accent="rose" />
        <MetricCard testid="ov-conversion" label="Chat → Pay Conversion" value={conv.rate_pct == null ? "N/A" : `${conv.rate_pct}%`} tip={TIPS.conversion}
          hint={conv.chat_users ? `${num(conv.chat_users_paid)} of ${num(conv.chat_users)} AI-chat users paid` : "No AI-chat users in range"} accent="navy" />
      </div>

      <SectionTitle testid="section-unit-economics" note="Per snapshot-backed paid consultation · refunded excluded">Unit economics</SectionTitle>
      {hasSnapshots ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <MetricCard testid="ue-spend" label="Avg Customer Spend" value={paise(u.avg_customer_spend_paise)} tip={TIPS.gmv} />
          <MetricCard testid="ue-fee" label="Avg Lawyer Fee" value={paise(u.avg_lawyer_fee_paise)} tip={TIPS.fees} />
          <MetricCard testid="ue-margin" label="Avg Advoset Margin" value={paise(u.avg_gross_margin_paise)} tip={TIPS.margin} negative={u.avg_gross_margin_paise < 0} />
          <MetricCard testid="ue-expense" label="Avg Other Expense" value={paise(u.avg_other_expense_paise)} tip={TIPS.expenses} />
          <MetricCard testid="ue-contribution" label="Avg Est. Contribution" value={paise(u.avg_contribution_paise)} tip={TIPS.contribution} negative={u.avg_contribution_paise < 0} />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400 mb-8" data-testid="ue-empty">
          No snapshot-based pricing data yet. New level-priced consultations will appear here.
        </div>
      )}

      <SectionTitle testid="section-by-level" note="Level 11 shown only when actual custom-priced consultations exist">Pricing &amp; unit economics by level</SectionTitle>
      <div className="mb-8">
        <EconTable rows={levelRows} firstCol={{ key: "level", label: "Level", render: (r) => r.label }} sortable testid="econ-level-table" />
      </div>

      <SectionTitle testid="section-by-mode">Economics by mode</SectionTitle>
      <div className="mb-8">
        <EconTable rows={modeRows} firstCol={{ key: "mode", label: "Mode", render: (r) => r.label }} testid="econ-mode-table" />
      </div>

      <SectionTitle testid="section-by-duration" note="15 min = 1× · 30 min = 2×">Economics by duration</SectionTitle>
      <div className="mb-8">
        <EconTable rows={durRows} firstCol={{ key: "duration_minutes", label: "Duration", render: (r) => r.label }} testid="econ-duration-table" />
      </div>

      <SectionTitle testid="section-marketplace" note="Lifetime">Marketplace</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="mkt-users" label="Total Users" value={num(s.users)} hint="Lifetime sign-ups" accent="navy" />
        <StatCard testid="mkt-lawyers" label="Lawyers" value={`${num(s.active_lawyers)} / ${num(s.lawyers)}`} hint={`${s.verified_lawyers} verified`} accent="gold" />
        <StatCard testid="mkt-articles" label="Articles" value={num(s.articles)} hint="Knowledge hub" accent="emerald" />
        <StatCard testid="mkt-chats" label="AI Chats" value={num(s.chats)} hint={`${num(s.queries)} queries asked`} accent="rose" />
      </div>

      <SectionTitle testid="section-kyc" note="Lifetime">Onboarding &amp; KYC</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="kyc-pending" label="KYC Pending Review" value={num(s.kyc?.pending_reviews)} hint="Awaiting reviewer" accent="gold" />
        <StatCard testid="kyc-approved-24h" label="KYC Approved (24h)" value={num(s.kyc?.approved_24h)} hint={`${num(s.kyc?.approved_total)} lifetime`} accent="emerald" />
        <StatCard testid="kyc-rejected-24h" label="KYC Rejected (24h)" value={num(s.kyc?.rejected_24h)} hint={`${num(s.kyc?.rejected_total)} lifetime`} accent="rose" />
        <StatCard testid="kyc-avg-review" label="Avg Review Time" value={
          s.kyc?.avg_review_seconds == null ? "—" :
            s.kyc.avg_review_seconds < 60 ? `${s.kyc.avg_review_seconds}s` :
            s.kyc.avg_review_seconds < 3600 ? `${Math.round(s.kyc.avg_review_seconds / 60)}m` :
            `${(s.kyc.avg_review_seconds / 3600).toFixed(1)}h`
        } hint="Last 200 decisions" accent="navy" />
        <StatCard testid="apps-pending" label="Applications Pending" value={num(s.applications?.pending)} hint="Self-serve queue" accent="gold" />
        <StatCard testid="apps-approved" label="Applications Approved" value={num(s.applications?.approved)} hint="Lifetime" accent="emerald" />
        <StatCard testid="apps-rejected" label="Applications Rejected" value={num(s.applications?.rejected)} hint="Lifetime" accent="rose" />
      </div>
      </div>
    </>
  );
}
